import React from "react";
import { NavLink, useHistory } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

import LoadingIndicator from "../../components/LoadingIndicator";
import useFetchTasks from "../../hooks/useFetchTasks";
import { useTaskFilters } from "../../context/TaskFilterContext";
import { truncateText } from "../../utils/textUtils";
import { groupTasks, sortTasks } from "../../utils/taskGroupingAndSorting";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import useTaskDeletion from "../../hooks/useTaskDeletion";
import { useRedirect } from "../../hooks/useRedirect";

import styles from "../../styles/pages/TasksPage.module.css";
import btnStyles from "../../styles/general/Button.module.css";

const TasksPage = () => {
  useRedirect("loggedOut");
  const history = useHistory();
  const { tasks, hasLoaded, setTasks } = useFetchTasks(false);
  const { groupBy, sortBy, order } = useTaskFilters();
  const {
    showDeleteModal,
    taskToDelete,
    setShowDeleteModal,
    handleDeleteClick,
    handleConfirmDelete,
  } = useTaskDeletion(setTasks);

  // Apply grouping & sorting logic
  const groupedTasks = groupTasks(tasks, groupBy).map(({ group, tasks }) => ({
    group,
    tasks: [...tasks].sort((a, b) => sortTasks(a, b, sortBy, order)), // Sort tasks inside groups
  }));

  return (
    <Container fluid className={styles.TaskContainer}>
      {!hasLoaded ? (
        <LoadingIndicator spinner message="Loading tasks..." />
      ) : tasks.length === 0 ? (
        <>
          <h1 className={styles.Heading}>All Tasks</h1>
          <p className={styles.NoTasks}>No tasks available.</p>
        </>
      ) : (
        <>
          {/* Delete Confirmation Modal */}
          <DeleteConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleConfirmDelete}
            taskTitle={taskToDelete?.title}
          />

          {groupedTasks.map(({ group, tasks }) => (
            <div key={group} className={styles.GroupContainer}>
              <h2 className={styles.GroupHeading}>{group}</h2>

              <Row className={styles.TaskRow}>
                {tasks.map((task) => (
                  <Col
                    key={task.id}
                    lg={4}
                    md={6}
                    xs={12}
                    className={styles.TaskCol}
                  >
                    <NavLink
                      to={`/tasks/${task.id}`}
                      className={styles.TaskLink}
                    >
                      <Card className={styles.TaskCard}>
                        <Card.Body className={styles.TaskCardBody}>
                          {/* Button Container Positioned to the Top Right */}
                          <div className={styles.ButtonContainer}>
                            <Button
                              className={btnStyles.EditButton}
                              aria-label="edit button"
                              onClick={(e) => {
                                e.preventDefault();
                                history.push(`/tasks/${task.id}/edit`);
                              }}
                            >
                              <i className="fa-solid fa-pen-to-square"></i>
                            </Button>

                            <Button
                              className={btnStyles.DeleteButton}
                              aria-label="delete button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeleteClick(e, task);
                              }}
                            >
                              <i className="fa-solid fa-trash-can"></i>
                            </Button>
                          </div>

                          <Card.Title className={styles.TaskTitle}>
                            {truncateText(task.title, 15)}
                          </Card.Title>
                          <Card.Text className={styles.TaskDetails}>
                            <span className={styles.TaskCategory}>
                              {" "}
                              {truncateText(task.category_name, 15)}
                            </span>
                            <span className={styles.TaskStatus}>
                              {" "}
                              {task.status}
                            </span>
                            <span className={styles.TaskPriority}>
                              {task.priority}
                            </span>
                            <span className={styles.TaskDueDate}>
                              Due Date: {task.due_date || "N/A"}
                            </span>
                          </Card.Text>
                        </Card.Body>
                      </Card>
                    </NavLink>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </>
      )}
    </Container>
  );
};

export default TasksPage;
